import { useAuth, useSignUp } from '@clerk/clerk-react';
import { useNavigate } from '@tanstack/react-router';
import { AlertCircle, Lock, Mail, User } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Heading, Input, InputGroup, Text } from '@/shared/ui';
import { toSignUpErrorMessage } from '../lib/clerk-errors';
import { currentInvitationTicket } from '../lib/invitation-ticket';
import {
  clerkErrorFields,
  reportInviteTrace,
} from '../lib/report-invite-trace';
import css from './sign-up-form.module.scss';

type FieldErrors = {
  email?: string;
  password?: string;
};

export const SignUpForm = () => {
  const { isSignedIn } = useAuth();
  const { signUp, setActive } = useSignUp();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState('');
  // Read once on mount: redeeming the ticket rewrites the URL, and the value
  // must survive that.
  const [ticket] = useState(currentInvitationTicket);

  useEffect(() => {
    if (isSignedIn) {
      navigate({ to: '/' });
    }
  }, [isSignedIn, navigate]);

  // The invitee reached the form that can actually redeem their ticket. Fires
  // on a direct landing too (Clerk redirecting straight here), where the
  // sign-in page's own `link.opened` never runs.
  useEffect(() => {
    if (!ticket) return;
    reportInviteTrace('link.opened', {
      ticket,
      fields: { landed: 'sign-up' },
    });
  }, [ticket]);

  const validate = useCallback(() => {
    const errors: FieldErrors = {};
    // An invitation ticket carries its own verified email address; the field
    // is not rendered, so it must not be validated either.
    if (!ticket) {
      if (!email) errors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(email))
        errors.email = 'Please enter a valid email address';
    }
    if (!password) errors.password = 'Password is required';
    else if (password.length < 8)
      errors.password = 'Password must be at least 8 characters';
    return errors;
  }, [email, password, ticket]);

  const handleSignUp = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      setError('');
      const errors = validate();
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) return;

      // Traced only when it IS the ticket strategy: plain email sign-up is the
      // default and says nothing. The distinction still earns its place on a
      // failure, because "that email is taken" means opposite things in the two
      // branches — on a ticket it is OUR bug (the ticket should have carried
      // the address), on a plain sign-up it is an ordinary duplicate.
      // Declared outside the try so the catch below can report it too.
      const strategy = ticket ? 'ticket' : undefined;

      try {
        // Ticket strategy: Clerk already reserved this email when the
        // invitation was created, so re-sending it as a fresh `emailAddress`
        // is what produced "That email address is taken". Redeeming the
        // ticket instead binds the new account to the invited address (and
        // marks it verified) without a second identifier.
        reportInviteTrace('signup.attempted', {
          email: email || undefined,
          ticket,
          fields: { strategy },
        });
        const result = await signUp?.create(
          ticket
            ? {
                strategy: 'ticket',
                ticket,
                firstName: firstName || undefined,
                lastName: lastName || undefined,
                password,
              }
            : {
                firstName: firstName || undefined,
                lastName: lastName || undefined,
                emailAddress: email,
                password,
              }
        );
        // In the ticket branch the form never asked for an address, so Clerk's
        // own record of it is the only place to learn who this actually is.
        const resolvedEmail = result?.emailAddress || email || undefined;
        if (result?.status === 'complete' && result.createdSessionId) {
          reportInviteTrace('signup.ok', {
            email: resolvedEmail,
            ticket,
            fields: { strategy },
          });
          await setActive?.({ session: result.createdSessionId });
          navigate({ to: '/' });
        } else if (result?.status) {
          // Clerk needs something more (an email code, a CAPTCHA, a missing
          // field) and this form has no step for it: no error, no navigation.
          // Only when Clerk RETURNED a status — see the note in sign-in-form.
          reportInviteTrace('signup.stalled', {
            email: resolvedEmail,
            ticket,
            fields: { strategy, status: result.status },
          });
        }
      } catch (err) {
        const resolvedEmail = signUp?.emailAddress || email;
        reportInviteTrace('signup.failed', {
          email: resolvedEmail || undefined,
          ticket,
          fields: {
            strategy,
            ...clerkErrorFields(err),
          },
        });
        // `resolvedEmail` rather than `email`: in the ticket branch the local
        // state is empty, which rendered the invitation-only message as a bare
        // " is only allowed to register…" with no address at all.
        setError(
          toSignUpErrorMessage(err, resolvedEmail, 'Failed to create account')
        );
      }
    },
    [
      signUp,
      setActive,
      navigate,
      firstName,
      lastName,
      email,
      password,
      ticket,
      validate,
    ]
  );

  return (
    <form className={css.root} onSubmit={handleSignUp}>
      <div className={css.header}>
        <Heading as="h1" size="2xl">
          Hello!
        </Heading>
        <Text as="p" size="lg">
          Create your account.
        </Text>
        <Text as="p" variant="muted">
          {ticket
            ? 'Choose a password to finish accepting your invitation.'
            : 'Enter your details below to get started.'}
        </Text>
      </div>

      <div className={css.fields}>
        <InputGroup htmlFor="sign-up-first-name" label="First Name (Optional)">
          <Input
            autoComplete="given-name"
            id="sign-up-first-name"
            name="firstName"
            placeholder="John"
            trailingIcon={<User size={16} />}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </InputGroup>

        <InputGroup htmlFor="sign-up-last-name" label="Last Name (Optional)">
          <Input
            autoComplete="family-name"
            id="sign-up-last-name"
            name="lastName"
            placeholder="Doe"
            trailingIcon={<User size={16} />}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </InputGroup>
      </div>

      <div className={css.fields}>
        {!ticket && (
          <InputGroup
            error={fieldErrors.email}
            htmlFor="sign-up-email"
            label="Email *"
          >
            <Input
              autoComplete="email"
              id="sign-up-email"
              name="email"
              placeholder="youremail@gmail.com"
              required
              trailingIcon={<Mail size={16} />}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </InputGroup>
        )}

        <InputGroup
          error={fieldErrors.password}
          htmlFor="sign-up-password"
          label="Password *"
        >
          <Input
            autoComplete="new-password"
            id="sign-up-password"
            name="password"
            placeholder="Start typing..."
            required
            trailingIcon={<Lock size={16} />}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </InputGroup>
      </div>

      {/* One action only: submitting creates the account AND activates the
          session, so the screen finishes registration and login in a single
          step. A separate "Sign In" escape hatch was a dead end here — the
          invitee has no password to sign in with yet. */}
      <div className={css.actions}>
        <Button size="xl" type="submit" variant="dark">
          Sign In
        </Button>
      </div>

      <div className={css.captcha} id="clerk-captcha" />

      {error && <Alert icon={<AlertCircle size={16} />}>{error}</Alert>}
    </form>
  );
};
